import { updateSlideInDeck } from "../editor/deckEditor.js";
import { arrangeSlideText } from "../layout/autoArrange.js";
import { effectiveThemeForSlide } from "../layout/compositionLayout.js";
import { applyTextRasterGeneration } from "../illustration/textRaster.js";
import { setSlideBackgroundKey } from "./backgroundService.js";
import { setRefineInstructions, } from "./slideImageEditService.js";
import { deckBackgroundColor, getRasterBackdrop, } from "../illustration/rasterBackdrop.js";
import { getLayerCutout } from "./slideImageService.js";
import { applySpeakerScriptText } from "../speaker/speakerScriptService.js";
function editTargetFromJob(target) {
    if (!target.slideId)
        return null;
    const kind = target.editKind ?? "layer";
    if (kind === "background")
        return { kind: "background", slideId: target.slideId };
    if (!target.layerId)
        return null;
    if (kind === "textRaster") {
        return { kind: "textRaster", slideId: target.slideId, layerId: target.layerId };
    }
    return { kind: "layer", slideId: target.slideId, layerId: target.layerId };
}
function applyLayerImage(deck, target, assetKey, applyMeta) {
    const slideId = target.slideId;
    const layerId = target.layerId;
    if (!slideId || !layerId)
        return deck;
    const persistedBaked = applyMeta?.persistedBaked;
    const persistedBackdrop = typeof applyMeta?.persistedBackdrop === "string" ? applyMeta.persistedBackdrop : undefined;
    return updateSlideInDeck(deck, slideId, (s) => ({
        ...s,
        layers: s.layers.map((l) => l.id === layerId
            ? {
                ...l,
                content: assetKey,
                style: {
                    ...l.style,
                    // New bitmap ⇒ any previous graphic crop no longer maps.
                    crop: undefined,
                    ...(persistedBaked
                        ? { bakedText: persistedBaked, textRender: "in_image" }
                        : {}),
                    ...(persistedBackdrop ? { rasterBackdrop: persistedBackdrop } : {}),
                },
            }
            : l),
    }));
}
function applyTextRaster(deck, target, assetKey, applyMeta) {
    const slideId = target.slideId;
    const layerId = target.layerId;
    if (!slideId || !layerId)
        return deck;
    const aspect = applyMeta?.aspect ?? "1:1";
    const backdrop = typeof applyMeta?.backdrop === "string"
        ? applyMeta.backdrop
        : deckBackgroundColor(deck.theme);
    return updateSlideInDeck(deck, slideId, (s) => ({
        ...s,
        layers: s.layers.map((l) => l.id === layerId ? applyTextRasterGeneration(l, assetKey, aspect, backdrop) : l),
    }));
}
function applySlideBackground(deck, target, assetKey, applyMeta) {
    const slideId = target.slideId;
    if (!slideId)
        return deck;
    const planKind = applyMeta?.planKind;
    const jobId = typeof applyMeta?.jobId === "string" ? applyMeta.jobId : undefined;
    const prompt = typeof applyMeta?.prompt === "string" ? applyMeta.prompt : undefined;
    const slideIndex = typeof applyMeta?.slideIndex === "number"
        ? applyMeta.slideIndex
        : deck.slides.findIndex((s) => s.id === slideId);
    if (planKind === "flat-plus-spot" || planKind === "content-slot") {
        return updateSlideInDeck(deck, slideId, (s) => {
            const imageLayer = s.layers.find((l) => l.type === "image");
            if (!imageLayer)
                return s;
            return {
                ...s,
                layers: s.layers.map((l) => l.id === imageLayer.id ? { ...l, content: assetKey } : l),
            };
        });
    }
    let result = setSlideBackgroundKey(deck, slideId, assetKey, jobId, prompt);
    if (slideIndex >= 0) {
        const slideAfter = result.slides[slideIndex];
        if (slideAfter) {
            const slideTheme = effectiveThemeForSlide(result.theme, slideAfter);
            const arranged = arrangeSlideText(slideAfter, slideTheme);
            result = updateSlideInDeck(result, slideId, () => arranged);
        }
    }
    return result;
}
function applyImageEdit(deck, target, assetKey, applyMeta) {
    const editTarget = editTargetFromJob(target);
    if (!editTarget)
        return deck;
    const instructions = typeof applyMeta?.instructions === "string" ? applyMeta.instructions : "";
    let next = instructions ? setRefineInstructions(deck, editTarget, instructions) : deck;
    if (editTarget.kind === "background") {
        const prompt = typeof applyMeta?.prompt === "string" ? applyMeta.prompt : undefined;
        const jobId = typeof applyMeta?.jobId === "string" ? applyMeta.jobId : undefined;
        return setSlideBackgroundKey(next, editTarget.slideId, assetKey, jobId, prompt);
    }
    if (editTarget.kind === "textRaster") {
        const layer = deck.slides
            .find((s) => s.id === editTarget.slideId)
            ?.layers.find((l) => l.id === editTarget.layerId);
        const backdrop = layer?.type === "text"
            ? getRasterBackdrop(layer, deck.theme)
            : deckBackgroundColor(deck.theme);
        return updateSlideInDeck(next, editTarget.slideId, (slide) => ({
            ...slide,
            layers: slide.layers.map((l) => l.id === editTarget.layerId
                ? {
                    ...l,
                    style: {
                        ...l.style,
                        rasterAssetKey: assetKey,
                        rasterStale: false,
                        rasterBackdrop: backdrop,
                        // New raster bitmap ⇒ drop stale graphic crop.
                        crop: undefined,
                    },
                }
                : l),
        }));
    }
    return updateSlideInDeck(next, editTarget.slideId, (slide) => ({
        ...slide,
        layers: slide.layers.map((l) => {
            if (l.id !== editTarget.layerId)
                return l;
            const layer = l;
            const backdrop = layer.type === "image" && getLayerCutout(layer)
                ? getRasterBackdrop(layer, deck.theme)
                : undefined;
            return {
                ...layer,
                content: assetKey,
                style: {
                    ...layer.style,
                    // Edited bitmap ⇒ drop stale graphic crop.
                    crop: undefined,
                    ...(backdrop ? { rasterBackdrop: backdrop } : {}),
                },
            };
        }),
    }));
}
function targetExists(deck, target) {
    if (!target.slideId)
        return true;
    const slide = deck.slides.find((s) => s.id === target.slideId);
    if (!slide)
        return false;
    if (!target.layerId)
        return true;
    return slide.layers.some((l) => l.id === target.layerId);
}
export function applySurfJobToDeck(deck, ctx) {
    if (!targetExists(deck, ctx.target))
        return deck;
    if (ctx.output.kind === "text") {
        if (ctx.op === "speaker.script" && ctx.target.slideId) {
            return applySpeakerScriptText(deck, ctx.target.slideId, ctx.output.text, ctx.applyMeta);
        }
        return deck;
    }
    const assetKey = ctx.output.assetKey;
    switch (ctx.op) {
        case "layer.image":
            return applyLayerImage(deck, ctx.target, assetKey, ctx.applyMeta);
        case "slide.raster.text":
            return applyTextRaster(deck, ctx.target, assetKey, ctx.applyMeta);
        case "slide.background":
            return applySlideBackground(deck, ctx.target, assetKey, ctx.applyMeta);
        case "image.edit":
            return applyImageEdit(deck, ctx.target, assetKey, ctx.applyMeta);
        case "style.preview":
            return { ...deck, theme: { ...deck.theme, previewS3Key: assetKey } };
        default:
            return deck;
    }
}
export const SURF_JOB_APPLIERS = {
    "layer.image": (deck, target, output, meta) => output.kind === "asset" ? applyLayerImage(deck, target, output.assetKey, meta) : deck,
    "slide.raster.text": (deck, target, output, meta) => output.kind === "asset" ? applyTextRaster(deck, target, output.assetKey, meta) : deck,
    "slide.background": (deck, target, output, meta) => output.kind === "asset" ? applySlideBackground(deck, target, output.assetKey, meta) : deck,
    "image.edit": (deck, target, output, meta) => output.kind === "asset" ? applyImageEdit(deck, target, output.assetKey, meta) : deck,
    outline: (deck) => deck,
    "speaker.script": (deck, target, output, meta) => output.kind === "text" && target.slideId ?
        applySpeakerScriptText(deck, target.slideId, output.text, meta)
        : deck,
    "style.preview": (deck, _target, output) => output.kind === "asset"
        ? { ...deck, theme: { ...deck.theme, previewS3Key: output.assetKey } }
        : deck,
};
